import { tokenStore } from './token-store';

export const API_URL =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ??
  'http://localhost:3000';

/**
 * A failed request, with the status and a message already pulled out of the
 * API's error envelope so callers never have to unpack it themselves.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Field-level validation messages, when the API sent an array. */
    readonly details: string[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isAuthError() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isConflict() {
    return this.status === 409;
  }
}

/**
 * The API's error envelope is `{ statusCode, message, timestamp }`, where
 * `message` is a string for thrown exceptions but a nested object with a
 * `message: string[]` for validation failures. Flatten both into one shape
 * so every error-handling path in the UI looks the same.
 */
function parseError(status: number, body: unknown): ApiError {
  const envelope = body as { message?: unknown } | null;
  const raw = envelope?.message;

  if (typeof raw === 'string') return new ApiError(status, raw);

  if (raw && typeof raw === 'object') {
    const nested = (raw as { message?: unknown }).message;
    if (Array.isArray(nested)) {
      const details = nested.map(String);
      return new ApiError(status, details[0] ?? 'Validation failed', details);
    }
    if (typeof nested === 'string') return new ApiError(status, nested);
  }

  if (Array.isArray(raw)) {
    const details = raw.map(String);
    return new ApiError(status, details[0] ?? 'Validation failed', details);
  }

  return new ApiError(status, `Request failed (${status})`);
}

/**
 * Resolves an upload path the API returned — `/uploads/avatars/x.jpg` — into
 * a URL this origin can actually load.
 *
 * The API stores and returns these as paths rather than absolute URLs, which
 * is right: the host is deployment-specific and baking it into a column
 * would survive a move to a CDN badly. But a path is relative to whoever
 * renders it, and that is this app on :3200, not the API. Without this an
 * avatar is a broken image — which on the application review screen is not
 * cosmetic, since the job there is deciding whether the photo and the ID
 * show the same person.
 */
export function uploadUrl(path: string | undefined): string | undefined {
  if (!path) return undefined;
  // An absolute URL (an S3 bucket, once uploads move there) is left alone.
  return /^https?:\/\//.test(path) ? path : `${API_URL}${path}`;
}

/** Called when the API rejects our token, so the app can send us to /login. */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler = () => {};
export const setUnauthorizedHandler = (fn: UnauthorizedHandler) => {
  onUnauthorized = fn;
};

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: QueryParams;
  /** Send this token instead of the stored one (used by the login flow). */
  token?: string | null;
  signal?: AbortSignal;
}

/**
 * Query parameters. Typed as a plain object rather than
 * `Record<string, unknown>` so the declared query interfaces — which have
 * no index signature — can be passed directly.
 */
export type QueryParams = object;

/** Drops undefined/null/empty values so blank filter inputs aren't sent. */
export function buildQuery(params: QueryParams = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.append(key, String(value));
  }
  const str = search.toString();
  return str ? `?${str}` : '';
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, token, signal } = options;
  const authToken = token !== undefined ? token : tokenStore.getAccessToken();

  const response = await fetch(`${API_URL}${path}${buildQuery(query)}`, {
    method,
    headers: {
      ...(body !== undefined && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? safeJson(text) : null;

  if (!response.ok) {
    const error = parseError(response.status, payload);
    // A 401 means the token is gone, expired, or the account was suspended
    // — all of which mean "sign in again", so handle it centrally rather
    // than in every caller.
    if (error.isAuthError) onUnauthorized();
    throw error;
  }

  return payload as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

/**
 * Fetches a response that is bytes rather than JSON — the one case being a
 * tutor's ID document, which is streamed by an audited admin endpoint.
 *
 * It cannot be an `<img src>` or a plain link: the endpoint needs the bearer
 * token, and putting the token in a URL would write it into history and into
 * every proxy log between here and the server. The caller gets a blob and is
 * responsible for revoking the object URL it makes from it.
 */
export async function requestBlob(path: string): Promise<Blob> {
  const authToken = tokenStore.getAccessToken();
  const response = await fetch(`${API_URL}${path}`, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
  });

  if (!response.ok) {
    const text = await response.text();
    const error = parseError(response.status, text ? safeJson(text) : null);
    if (error.isAuthError) onUnauthorized();
    throw error;
  }
  return response.blob();
}

export const api = {
  get: <T>(path: string, query?: QueryParams) => request<T>(path, { query }),
  post: <T>(path: string, body?: unknown, query?: QueryParams) =>
    request<T>(path, { method: 'POST', body, query }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

export async function requestForm<T>(path: string, form: FormData): Promise<T> {
  const authToken = tokenStore.getAccessToken();
  const response = await fetch(`${API_URL}${path}`, {
    method: 'POST', headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}, body: form,
  });
  const text = await response.text();
  if (!response.ok) {
    const error = parseError(response.status, text ? safeJson(text) : null);
    if (error.isAuthError) onUnauthorized();
    throw error;
  }
  return safeJson(text) as T;
}
